import React from 'react';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Monitor,
  Settings,
  LogOut,
  Play,
  Wifi,
  WifiOff,
  Clock,
  AlertTriangle,
  Info,
  AlertCircle,
  CheckSquare,
  Download,
  Eye,
  X,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';
import { SettingsConfirmationDialog } from './SettingsConfirmationDialog';
import axios from 'axios';
import mqtt from 'mqtt';

import Convert from "ansi-to-html";
import CookingSessions from './CookingSessions.jsx';
const activateMeterRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};

const adjustCreditsRules = {
  customerId: v => /^\d+$/.test(v) && typeof v === "string",
  cardId: v => /^\d+$/.test(v) && typeof v === "string",
  amount: v => typeof v === "number" && v > 0,
  unitPrice: v => typeof v === "number" && v > 0,
  paymentDate: v => Number.isInteger(v),
  meterType: v => typeof v === "string" && ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};
const changeBatteryRules = {
  batteryModel: v => typeof v === "string" && v.length > 0,
  batteryQuantity: v => Number.isInteger(v) && v > 0,
  batterySerialNumberList: v =>
    typeof v === "string" &&
    v.split(',').every(sn => typeof sn.trim() === "string" && sn.trim().length > 0),
  meterType: v => typeof v === "string" && ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};
const updateSettingsRules = {
  serverURL: v => typeof v === "string" && v.length > 0,
  updatePeriod: v => Number.isInteger(v) && v >= 60, // Assuming a minimum of 60 seconds
  startPeriod: v => Number.isInteger(v) && v >= 0,
  warningLowBatteryVoltage: v => typeof v === "number" && v > 0,
  warningLowCreditBalance: v => typeof v === "number" && v >= 0,
  warningLowGasMass: v => typeof v === "number" && v >= 0,
  currency: v => typeof v === "string" && v.length > 0,
  unitPrice: v => typeof v === "number" && v > 0,
  sensorSlope: v => typeof v === "number",
  sensorIntercept: v => typeof v === "number",
  operatingCompany: v => typeof v === "string" && v.length > 0,
  meterType: v => typeof v === "string" && ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};
const cookingSessionRules = {
  startTime: v => Number.isFinite(v),
  endTime: v => Number.isFinite(v),
  time_quality: v => Number.isInteger(v),
  startCredit: v => typeof v === "number",
  endCredit: v => typeof v === "number",
  startCumulativeMass: v => typeof v === "number",
  endCumulativeMass: v => typeof v === "number",
  gasRemaining: v => typeof v === "number",
  unitPrice: v => typeof v === "number" && v > 0,
  csrpSequenceNo: v => Number.isInteger(v),
  uuid: v => typeof v === "string" && v.length > 0,
  product_id: v => typeof v === "string" && v.length > 0,
  type: v => typeof v === "string" && ["UNIT", "UNKNOWN"].includes(v)
};

const openMeterLockRules = {
  meterType: v => typeof v === "string" && ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};
const closeMeterLockRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};
const openMeterValveRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};

const closeMeterValveRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};
const cylinderInstallRules = {
  cylinderSerial: v =>
    typeof v === "string" && v.length > 0,

  gasLevel: v =>
    typeof v === "number" && v >= 0,

  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};

const getSettingRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};

const getCookingSessionReportRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};

const getCumulativeReportRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};
const getHardwareReportRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};

const getLogsRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};
const selfTestRules = {
  meterType: v =>
    typeof v === "string" &&
    ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};

const adjustDataRules = {
  customerId: v =>
    typeof v === "string" && v.length > 0,

  cardId: v =>
    typeof v === "string" && v.length > 0,

  creditRemaining: v =>
    typeof v === "number" && v >= 0,

  gasRemaining: v =>
    typeof v === "number" && v >= 0,

  unitPrice: v =>
    typeof v === "number" && v >= 0,

  endCumulativeMass: v =>
    typeof v === "number" && v >= 0,

  endCumulativeCredit: v =>
    typeof v === "number" && v >= 0,

  cylinderSerial: v =>
    typeof v === "string" && v.length > 0,

  meterChangeDate: v =>
    typeof v === "number" && v > 0,

  meterType: v =>
    typeof v === "string" && ["P4-MQTT", "PX-MQTT", "P1-GPRS"].includes(v)
};

const cumulativeReportRules = {
  endCumulativeMass: v => typeof v === "number",            // float
  endCumulativeCredit: v => typeof v === "number",          // float
  gasRemaining: v => typeof v === "number",                 // float
  currentCredit: v => typeof v === "number",                // float
  cumlTimestamp: v => Number.isInteger(v),                  // unix timestamp
  uuid: v => typeof v === "string" && v.startsWith("sr_")   // sr string
};
const meterStatusRules = {
  lastCookingTime: v => Number.isInteger(v),          // unix
  gasRemaining: v => typeof v === "number",           // float
  creditRemaining: v => typeof v === "number",        // float
  mAhRemaining: v => Number.isInteger(v),             // int
  mAhUsed: v => Number.isInteger(v),                  // int
  mAhTotal: v => Number.isInteger(v),                 // int
  batteryLevel: v => typeof v === "number",           // float
  countValveCycle: v => Number.isInteger(v),          // int
  electronicValveStatus: v => typeof v === "string",
  gsmSignalIntensity: v => Number.isInteger(v),       // int
  cookingSessionSent: v => Number.isInteger(v),       // int
  cookingSessionCreated: v => Number.isInteger(v),    // int
  network_operator_id: v => /^\d+$/.test(v),          // int (string)
  latest_idle_voltage: v => Number.isInteger(v),      // int
  stm32_sos_sched_resets: v => Number.isInteger(v),   // int
  stm32_sos_resets_success: v => Number.isInteger(v), // int
  uuid: v => typeof v === "string" && v.startsWith("sr_"),
  cloud_voltages: v =>
    Array.isArray(v) && v.every(n => Number.isInteger(n)) // integer array
};

const hardwareStatusRules = {
  pcbVersion: v => typeof v === "string",
  B1FirmwareVersion: v => !isNaN(parseFloat(v)),            // float
  B2FirmwareVersion: v => !isNaN(parseFloat(v)),            // float
  modemFirmwareVersion: v => typeof v === "string",
  batteryModel: v => typeof v === "string",
  iflowSerialNumber: v => typeof v === "string",
  meterSerialNumber: v => /^\d+$/.test(v),                  // long integer as string
  ICCID: v => /^\d+$/.test(v),                              // long integer as string
  totalLogs: v => Number.isInteger(v),                      // integer
  otaStatus: v => typeof v === "string",
  calibrationVersion: v => Number.isInteger(v),             // integer
  uuid: v => typeof v === "string" && v.startsWith("sr_")   // sr string
};
class JobsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedLog: false,
      expandedType: "stm",
      loadingLogs: null,
      showConfirmDialog: false,
      selectedJobs: this.props.jobs.filter(job => job.selected),
      EspResponse: null,
      STMResponse: null,
      EspTimestamp: null,
      STMTimestamp: null,
      ActivateMeter: null,
      AdjustCredit: null,
      ChangeBattery: null,
      GetLogs: null,
      UpdateSettings: null,
      OpenMeterLock: null,
      AdjustData: null,
      Deactivate: null,
      ResetAll: null,
      OTA: null,
      CumulativeReport: null,
      cookingSessionsShow: null,
      ChildLock: null,
      OpenMeterLockReport: null,
      CloseMeterLockReport: null,
      OpenMeterValveReport: null,
      CloseMeterValveReport: null,
      ResetAllReport: null,
      DeactivateReport: null,
      OTAReport: null,
      CylinderInstall: null,
      GetSettings: null,
      CookingSession: null,
      MeterStatus: null,
      HardwareStatus: null,
      CookingSessionStatus: null,
      cookingSessionPage: false
    };
    this.convert = new Convert(); // create converter instance

  }
  componentDidMount() {
    this.handleConnect()
  };
  componentDidUpdate(prevProps, prevState) {
    if (prevState.loginResponse !== this.state.loginResponse) {

    }
  }
  genRanHex = size => [...Array(size)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');

  handleConnect = () => {
    console.log('handle connect');

    var clientId = this.genRanHex(12);
    const connectUrl = `ws://18.223.155.160:8000/mqtt`;
    this.setState({ connectStatus: connectUrl });
    const client = mqtt.connect(connectUrl, {
      clientId,
      clean: true,
      connectTimeout: 4000,
      username: 'admin_admin',
      password: 'admin_user',
      reconnectPeriod: 1000,
      keepalive: 30,
      protocolId: "MQTT",
      protocolVersion: 4,
      will: {
        topic: "WillMsg",
        payload: "Connection Closed abnormally..!",
        qos: 1,
        retain: false,
      },
      connectionStatus: false,
      rejectUnauthorized: false,
      UpdateSettings: true,
      Activate: true,
      CylinderInstall: true,
      AdjustCredit: true,
      ChildLock: true,
      ChangeBattery: true,
      CumulativeReport: true,
      HardwareStatus: true,
      GetSettings: true,
      MeterStatus: true,
      GetLogs: true,
      OpenMeterLock: true,
      AdjustData: true,
      Deactivate: true,
      ResetAll: true,
      OTA: true,
    })
    console.log('here : ');
    const substm = '#';
    // const subs_notify = 'security/notify' ;
    const pub = 'orca/kebs/activation/89234223';
    if (client) {
      client.on("connect", () => {
        this.setState({
          connectionStatus: true,
        });
        // client.publish(pub, { Status: this.state.VendorPhoneNumber})
        console.log(": connect :");
        // client.publish(pub, '{"Status":"' + this.state.VendorPhoneNumber + '"}')
        client.subscribe(substm, function (err) {
          if (!err) {
            // Publish a message to a topic
            // client.publish(pub, 'GetPowerStatus')
            // client.publish(pub, 'inputs')
          }
        })

      });
      client.on("error", (err) => {
        console.log('mqtt error' + err);
        this.setState({
          connectionStatus: false,
        });
        client.end();
      });
      client.on("reconnect", () => {
        console.log('mqtt reconnecting');
      });
      client.on("message", (topic, message) => {
        const payload = { topic, message: message.toString() };
        // console.log("payload", payload.message);
        // console.log("message", payload);
        if (topic === "Gas/STM/data") {
          const now = new Date();

          this.setState({
            STMTimestamp: now.toLocaleString(),

            STMResponse: this.state.STMResponse + payload.message
          });
        } else if (topic === "Gas/ESP/data") {
          const now = new Date();
          if (payload.message?.includes("cookingSessions")) {
            console.log(" cookingSessions payload.message : ", payload.message)

            const payloadnew = this.extractJsonAfterKeyword(payload.message, "cookingSessions");
            console.log(" cookingSessions : ", payloadnew)

            const iscookingSessionsValid = this.validateArrayWithRules(payloadnew, cookingSessionRules);
            console.log(" iscookingSessionsValid : ", iscookingSessionsValid)

            // this.setState({
            //   cookingSessionsShow: iscookingSessionsValid,
            // });

          }

          if (payload.message?.includes("activateMeter")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "activateMeter");
            const isActivateMeterValid = this.validateObject(payloadnew, activateMeterRules);
            this.setState({
              ActivateMeter: isActivateMeterValid,
            });

          }
          if (payload.message?.includes("adjustCredits")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "adjustCredits");
            const isAdjustCreditsValid = this.validateObject(payloadnew, adjustCreditsRules);
            this.setState({
              AdjustCredit: isAdjustCreditsValid,
            });

          }
          if (payload.message?.includes("changeBattery")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "changeBattery");
            const isChangeBatteryValid = this.validateObject(payloadnew, changeBatteryRules);
            this.setState({
              ChangeBattery: isChangeBatteryValid,
            });

          }
          if (payload.message?.includes("updateSettings")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "updateSettings");
            const isUpdateSettingsValid = this.validateObject(payloadnew, updateSettingsRules);
            this.setState({
              UpdateSettings: isUpdateSettingsValid,
            });

          }
          if (payload.message?.includes("openMeterLock")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "openMeterLock");
            const isOpenMeterLockValid = this.validateObject(payloadnew, openMeterLockRules);
            this.setState({
              OpenMeterLockReport: isOpenMeterLockValid,
            });
          }
          if (payload.message?.includes("closeMeterLock")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "closeMeterLock");
            const isCloseMeterLockValid = this.validateObject(payloadnew, closeMeterLockRules);
            this.setState({
              CloseMeterLockReport: isCloseMeterLockValid,
            });
          }
          if (payload.message?.includes("openMeterValve")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "openMeterValve");
            const isOpenMeterValveValid = this.validateObject(payloadnew, openMeterValveRules);
            this.setState({
              OpenMeterValveReport: isOpenMeterValveValid,
            });
          }
          if (payload.message?.includes("closeMeterValve")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "closeMeterValve");
            const isCloseMeterValveValid = this.validateObject(payloadnew, closeMeterValveRules);
            this.setState({
              CloseMeterValveReport: isCloseMeterValveValid,
            });
          }
          if (payload.message?.includes("cylinderInstall")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "cylinderInstall");
            const isCylinderInstallValid = this.validateObject(payloadnew, cylinderInstallRules);
            this.setState({
              CylinderInstall: isCylinderInstallValid,
            });
          }
          if (payload.message?.includes("getSetting")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "getSetting");
            const isGetSettingValid = this.validateObject(payloadnew, getSettingRules);
            this.setState({
              GetSettings: isGetSettingValid,
            });
          }
          if (payload.message?.includes("getCookingSessionReport")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "getCookingSessionReport");
            const isCookingSessionValid = this.validateObject(payloadnew, getCookingSessionReportRules);
            this.setState({
              CookingSession: isCookingSessionValid,
            });
          }
          if (payload.message?.includes("cumulativeReport")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "cumulativeReport");
            const isCumulativeValid = this.validateObject(payloadnew, cumulativeReportRules);
            this.setState({
              CumulativeReport: isCumulativeValid,
            });

          }
          if (payload.message?.includes(`"meterStatus"`)) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, `"meterStatus"`);
            console.log("meterStatus", payloadnew);
            const isMeterStatusValid = this.validateObject(payloadnew, meterStatusRules);
            this.setState({
              MeterStatus: isMeterStatusValid,
            });
          }

          if (payload.message?.includes("hardwareReport")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "hardwareReport");
            const isHardwareValid = this.validateObject(payloadnew, hardwareStatusRules);
            this.setState({
              HardwareStatus: isHardwareValid,
            });
          }
          if (payload.message?.includes("getLogs")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "getLogs");
            const isLogsRulesValid = this.validateObject(payloadnew, getLogsRules);
            this.setState({
              GetLogs: isLogsRulesValid,
            });
          }
          if (payload.message?.includes("selfTest")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "selfTest");
            const isSelfTestValid = this.validateObject(payloadnew, selfTestRules);
            this.setState({
              HardwareStatus: isSelfTestValid,
            });
          }
          if (payload.message?.includes("adjustData")) {
            const payloadnew = this.extractJsonAfterKeyword(payload.message, "adjustData");
            const isAdjustDataValid = this.validateObject(payloadnew, adjustDataRules);
            this.setState({
              HardwareStatus: isAdjustDataValid,
            });
          }

          this.setState({
            EspTimestamp: now.toLocaleString(),
            EspResponse: this.state.EspResponse + payload.message
          });
        }




        // const { messages } = this.state;
        // if (payload.topic) {
        //   const changedMessages = messages.concat([payload]);
        //   this.setState({ messages: changedMessages });
        // }
      });
    }
  };
  validateArrayWithRules = (arr, rules) => {
    if (!Array.isArray(arr)) {
      return {
        isValid: false,
        error: "Expected an array"
      };
    }

    const results = arr?.map((item, index) => {
      console.log(" item   : ", item)
      const validation = this.validateObject(item, rules);

      return {
        index,
        uuid: item.uuid,
        ...validation
      };
    });

    return {
      isValid: results.every(r => r.isValid),
      results
    };
  }
  validateObject = (obj, rules) => {
    for (let key in rules) {
      const isValid = rules[key](obj[key]);
      if (!isValid) {
        console.error(`❌ ${key} failed validation. Value:`, obj[key]);
        return false;
      }
    }
    return true;
  }
  extractJsonAfterKeyword = (str, keyword) => {
    // Find keyword location
    const index = str.indexOf(keyword);
    if (index === -1) return null;

    // Find first JSON start symbol after keyword
    const objectStart = str.indexOf("{", index);
    const arrayStart = str.indexOf("[", index);

    // Determine which comes first
    let jsonStart;
    let openingChar;
    let closingChar;

    if (objectStart === -1 && arrayStart === -1) return null;

    if (arrayStart === -1 || (objectStart !== -1 && objectStart < arrayStart)) {
      jsonStart = objectStart;
      openingChar = "{";
      closingChar = "}";
    } else {
      jsonStart = arrayStart;
      openingChar = "[";
      closingChar = "]";
    }

    let depth = 0;
    let insideString = false;

    for (let i = jsonStart; i < str.length; i++) {
      const char = str[i];
      const prev = str[i - 1];

      // Handle string quotes
      if (char === '"' && prev !== "\\") {
        insideString = !insideString;
      }

      if (!insideString) {
        if (char === openingChar) depth++;
        if (char === closingChar) depth--;
      }

      // Completed structure
      if (depth === 0) {
        const jsonString = str.slice(jsonStart, i + 1);
        try {
          return JSON.parse(jsonString);
        } catch (e) {
          return jsonString; // return raw text if parsing fails
        }
      }
    }

    return null; // No closing bracket found
  };

  // extractJsonAfterKeyword = (str, keyword) => {
  //   // Find where the keyword occurs
  //   const index = str.indexOf(keyword);
  //   if (index === -1) return null;

  //   // Find first JSON object "{"
  //   const jsonStart = str.indexOf("{", index);
  //   if (jsonStart === -1) return null;

  //   let depth = 0;
  //   let insideString = false;

  //   for (let i = jsonStart; i < str.length; i++) {
  //     const char = str[i];
  //     const prev = str[i - 1];

  //     // Toggle string detection (ignore escaped quotes)
  //     if (char === '"' && prev !== "\\") {
  //       insideString = !insideString;
  //     }

  //     if (!insideString) {
  //       if (char === "{") depth++;
  //       if (char === "}") depth--;
  //     }

  //     if (depth === 0) {
  //       const jsonString = str.slice(jsonStart, i + 1);
  //       try {
  //         return JSON.parse(jsonString);
  //       } catch (e) {
  //         return null;
  //       }
  //     }
  //   }
  //   return null;
  // }
  onMeterRestart = () => {
    console.log('handle connect');

    var clientId = this.genRanHex(12);
    const connectUrl = `ws://18.223.155.160:8000/mqtt`;
    this.setState({ connectStatus: connectUrl });
    const client = mqtt.connect(connectUrl, {
      clientId,
      clean: true,
      connectTimeout: 4000,
      username: 'admin_admin',
      password: 'admin_user',
      reconnectPeriod: 1000,
      keepalive: 30,
      protocolId: "MQTT",
      protocolVersion: 4,
      will: {
        topic: "WillMsg",
        payload: "Connection Closed abnormally..!",
        qos: 1,
        retain: false,
      },
      rejectUnauthorized: false,
    })
    console.log('here : ');
    // const subs = 'Gas/control';
    // const subs_notify = 'security/notify' ;
    const pub = 'Gas/control';
    if (client) {
      client.on("connect", () => {
        client.publish(pub, "RESET_STM")
        console.log(": connect :");
        // client.publish(pub, '{"Status":"' + this.state.VendorPhoneNumber + '"}')

      });
      client.on("error", (err) => {
        console.log('mqtt error' + err);
        client.end();
      });
      client.on("reconnect", () => {
        console.log('mqtt reconnecting');
      });

    }
  };
  onJobSelectionChange = async () => {
    const url = "https://3m1u2yiiu8.execute-api.eu-west-1.amazonaws.com/dev/jobs/meter/24100054692/updateSettings";
    // const url = 'http://localhost:4000/api/updateSettings';
    const payloadData = {
      serverURL: "3m1u2yiiu8.execute-api.eu-west-1.amazonaws.com",
      updatePeriod: 360,
      startPeriod: 180,
      warningLowBatteryVoltage: 3.5,
      warningLowCreditBalance: 10.00,
      warningLowGasMass: 2.5,
      currency: "KSh",
      unitPrice: 297,
      sensorSlope: 1,
      sensorIntercept: 1,
      operatingCompany: "mgasKE",
      meterType: "PX-MQTT"
    }

    this.setState({ loading: true, error: null });

    try {
      // 1. Get temporary IAM credentials from Cognito Identity Pool
      // const creds = await Auth.currentCredentials();
      // const { accessKeyId, secretAccessKey, sessionToken } = creds;

      // // 2. Create SigV4 signer
      // const signer = new SignatureV4({
      //   credentials: {
      //     accessKeyId,
      //     secretAccessKey,
      //     sessionToken,
      //   },
      //   region: "eu-west-1",
      //   service: "execute-api",
      //   sha256: Sha256,
      // });

      // 3. Build the request
      // const request = new HttpRequest({
      //   method: "GET",
      //   protocol: "https:",
      //   path: "/dev/jobs/meter/24100054692/updateSettings",
      //   headers: {
      //     host: "3m1u2yiiu8.execute-api.eu-west-1.amazonaws.com",
      //   },
      //   hostname: "3m1u2yiiu8.execute-api.eu-west-1.amazonaws.com",
      // });

      // // 4. Sign the request
      // const signedRequest = await signer.sign(request);

      // // 5. Call the API
      // const url = `https://${signedRequest.hostname}${signedRequest.path}`;
      // const response = await fetch(url, {
      //   method: signedRequest.method,
      //   headers: signedRequest.headers,
      // });

      // if (!response.ok) {
      //   throw new Error(`HTTP error! Status: ${response.status}`);
      // }

      // const data = await response.json();
      // this.setState({ jobs: data, loading: false });
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  };


  setCookingSessionPage = () => {
    this.setState({ cookingSessionPage: true });
  };

  getLogIcon = (level) => {
    switch (level) {
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
  };
  getLogBadgeVariant = (level) => {
    switch (level) {
      case 'info':
        return 'default';
      case 'warning':
        return 'secondary';
      case 'error':
        return 'destructive';
    }
  };
  handleDownloadexpandedLog = async (logFile) => {
    if (this.state?.expandedType === "esp") {
      this.downloadFile(this.state.EspResponse);
    } else {
      this.downloadFile(this.state.STMResponse);
    }
  };
  handleDownloadLog = async (logFile) => {
    if (logFile?.id === "esp32-logs") {
      this.downloadFile(this.state.EspResponse);
    } else {
      this.downloadFile(this.state.STMResponse);
    }
    // if (!logFile.fullContent) {
    //   this.setState({
    //     loadingLogs: logFile.id,
    //   })
    //   await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate loading
    //   this.props.onRequestLogs(logFile.id);
    //   this.setState({
    //     loadingLogs: null,
    //   })
    //   setTimeout(() => {
    //     const updatedLogFile = this.props.meterLogFiles.find(file => file.id === logFile.id);
    //     if (updatedLogFile?.fullContent) {
    //       this.downloadFile(updatedLogFile);
    //     }
    //   }, 100);
    // } else {
    //   this.downloadFile(logFile);
    // }
  };

  downloadFile = (logFile) => {
    const blob = new Blob([logFile], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `log.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  handleViewLog = async (logFileId) => {

    if (logFileId === "esp32-logs") {
      this.setState({
        expandedType: "esp",
        expandedLog: true
      })
    } else {
      this.setState({
        expandedType: "stm",
        expandedLog: true
      })
    }

  };

  handleCloseExpandedLog = () => {
    this.setState({ expandedLog: false })
  };

  handleRefreshExpandedLogs = async (logFileId) => {
    if (this.state?.expandedType === "esp") {
      this.setState({
        EspResponse: null,
      })
    } else {
      this.setState({
        STMResponse: null,
      })
    }
    // this.setState({
    //   loadingLogs: logFileId,
    // })
    // await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate loading
    // this.props.onRequestLogs(logFileId);
    // this.setState({
    //   loadingLogs: null,
    // })
  };
  handleRefreshLogs = async (logFileId) => {
    if (logFileId === "esp32-logs") {
      this.setState({
        EspResponse: null,
      })
    } else {
      this.setState({
        STMResponse: null,
      })
    }
    // this.setState({
    //   loadingLogs: logFileId,
    // })
    // await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate loading
    // this.props.onRequestLogs(logFileId);
    // this.setState({
    //   loadingLogs: null,
    // })
  };

  handleExecuteClick = () => {
    if (this.state.selectedJobs.length === 0) return;
    this.setState({ showConfirmDialog: true })
  };

  handleConfirmExecute = (confirmedSettings) => {
    this.props.onExecuteJobs(confirmedSettings);
  };
  backToJobs = () => {
    this.setState({
      cookingSessionPage: false,
    });
  }

  setInputValue(property, val) {
    this.setState({
      [property]: val,
    });
  }
  doNewLogin = () => {

    const data = {
      "email": this.state.email,
      "password": this.state.password,
      "token": "",

    };
    console.log("data");
    console.log(data);
    //  const url = 'https://school-api.atomiot.co.ke/v1/auth/login';
    const url = 'https://api.greenthumb.co.ke/api/vendors/login';
    axios.post(url, data).then((response) => {
      console.log(response.data);
      this.setState({
        loginResponse: response.data,
      });
    });

  }
  stripAnsi(str) {
    if (!str) return "";
    return str.replace(/\x1b\[[0-9;]*m/g, "");
  }

  render() {
    // console.log(" this.props.jobs : ", this.props.jobs);
    if (this.state.expandedLog) {


      return (
        <div className="min-h-screen bg-background flex flex-col">
          {/* Header */}
          <div className="border-b bg-card flex-shrink-0">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center space-x-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={this.handleCloseExpandedLog}
                    className="flex items-center space-x-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span>Back to Logs</span>
                  </Button>
                  <div className="flex items-center space-x-2">
                    <Monitor className="h-6 w-6" />
                    {/* <h1 className="text-lg font-medium">{logFile.name} - Full Log View</h1> */}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => this.handleRefreshExpandedLogs()}
                    // disabled={loadingLogs === logFile.id}
                    className="flex items-center space-x-1"
                  >
                    {/* <RefreshCw className={`h-4 w-4 ${loadingLogs === logFile.id ? 'animate-spin' : ''}`} /> */}
                    <span>Refresh</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => this.handleDownloadexpandedLog()}
                    // disabled={loadingLogs === logFile.id}
                    className="flex items-center space-x-1"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={this.handleCloseExpandedLog}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Full Log Content */}
          <div className="container mx-auto px-4 py-6 flex-1 flex flex-col min-h-0">
            <Card className="flex-1 flex flex-col min-h-0">
              <CardHeader className="flex-shrink-0">
                {/* <CardTitle>{logFile.name}</CardTitle> */}
                <CardDescription>
                  {/* Last updated: {logFile.lastUpdated} • Full log file content */}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col min-h-0 pt-0">
                <ScrollArea className="flex-1 min-h-0">
                  {this.state.expandedType === "esp" ? (
                    <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                      {this.stripAnsi(this.state.EspResponse)}
                    </pre>
                  ) : this.state.expandedType === "stm" ? (
                    <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                      {this.stripAnsi(this.state.STMResponse)}
                    </pre>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center">
                        <Monitor className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No log data available</p>
                        <p className="text-sm">Execute jobs or refresh to generate logs</p>
                      </div>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }
    if (this.state.cookingSessionPage) {
      return <CookingSessions backToJobs={this.backToJobs} onNavigateToJobs={this.props.onNavigateToJobs} onLogout={this.props.onLogout} />;
    }
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* <SettingsConfirmationDialog
          open={this.state.showConfirmDialog}
          onOpenChange={this.setState({ showConfirmDialog: true})}
          settings={this.props.settings}
          selectedJobs={this.state.selectedJobs}
          onConfirmExecute={this.handleConfirmExecute}
        /> */}

        {/* Header */}
        <div className="border-b bg-card flex-shrink-0">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Monitor className="h-6 w-6" />
                  <h1 className="text-lg font-medium">Jobs Monitor</h1>
                </div>

                {/* Connection Status */}
                <div className="flex items-center space-x-2">
                  {this.state.connectionStatus === 'connected' ? (
                    <>
                      <Wifi className="h-4 w-4 text-green-500" />
                      <Badge variant="outline" className="text-green-700 border-green-200">
                        Connected
                      </Badge>
                    </>
                  ) : (
                    <>
                      <WifiOff className="h-4 w-4 text-red-500" />
                      <Badge variant="outline" className="text-red-700 border-red-200">
                        Disconnected
                      </Badge>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* Meter Number Input */}
                <div className="flex items-center space-x-2">
                  <Label htmlFor="meterNumber" className="text-sm whitespace-nowrap">
                    Meter Number:
                  </Label>
                  <Input
                    id="meterNumber"
                    value={this.state.meterNumber}
                    // onChange={(e) => onMeterNumberChange(e.target.value)}
                    placeholder="Enter meter number"
                    className="w-40"
                  />
                </div>

                {/* Navigation */}
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={this.onMeterRestart}
                    className="flex items-center space-x-1"
                    style={{ backgroundColor: '#1a73e8', color: '#ffffff' }}
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Trigger Connection </span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={this.props.onNavigateToSettings}
                    className="flex items-center space-x-1"
                    style={{ backgroundColor: '#1a73e8', color: '#ffffff' }}
                  >
                    <Settings className="h-4 w-4" />
                    <span>Settings</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={this.props.onLogout}
                    className="flex items-center space-x-1"
                    style={{ backgroundColor: '#ea4335', color: '#ffffff' }}

                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-6 flex-1 flex flex-col min-h-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[80vh] min-h-0">
            <div className="lg:col-span-1 flex flex-col min-h-0">
              <Card className="flex-1 flex flex-col min-h-0">
                <CardHeader className="flex-shrink-0 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Available Jobs</CardTitle>
                      <CardDescription>
                        Select jobs to execute ({this.state.selectedJobs.length} selected)
                      </CardDescription>
                    </div>
                    {/* Select All Button */}
                    <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2">
                      <Checkbox
                        id="select-all"
                        checked={this.state.selectAllActive}
                      // onCheckedChange={(checked) => onSelectAllChange(checked)}
                      />
                      <div className="flex items-center space-x-1">
                        <CheckSquare className="h-4 w-4 text-blue-600" />
                        <Label
                          htmlFor="select-all"
                          className="text-sm font-medium text-blue-700 dark:text-blue-300 cursor-pointer whitespace-nowrap"
                        >
                          Select All
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col min-h-0 pt-0">
                  {/* The ScrollArea needs flex-1 and min-h-0 to properly expand and scroll */}
                  <ScrollArea className="flex-1 min-h-0 p-4  rounded-md border bg-gray-50 dark:bg-gray-900">  <div className="space-y-3 pr-2">
                    {this.props.jobs.map((job) => (
                      <div
                        key={job.id}
                        className="flex items-start space-x-3 p-3 rounded-lg border bg-card hover:bg-accent-50 transition-colors"
                        onClick={() => {
                          job.name === "Cooking Sessions" ? this.setCookingSessionPage() : this.setJobSelection(job)
                        }}
                      >
                        <Checkbox
                          id={job.id}
                          checked={job.selected}
                          onCheckedChange={(checked) =>
                            this.onJobSelectionChange(job, checked)
                          }
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <Label
                            htmlFor={job.id}
                            className="cursor-pointer"
                          >
                            {job.name}
                          </Label>



                          <p className="text-sm text-muted-foreground mt-1">
                            {job.description}
                          </p>
                        </div>
                        {job.name === "Update Settings" && (
                          <Badge
                            className={`text-xs ${this.state.UpdateSettings === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.UpdateSettings === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.UpdateSettings === null ? "Not Tested" : this.state.UpdateSettings === true ? "Pass" : "Fail"}

                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Activate" && (
                          <Badge
                            className={`text-xs ${this.state.ActivateMeter === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.ActivateMeter === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.ActivateMeter === null ? "Not Tested" : this.state.ActivateMeter === true ? "Pass" : "Fail"}

                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Cylinder Install" && (
                          <Badge
                            className={`text-xs ${this.state.CylinderInstall === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.CylinderInstall === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.CylinderInstall === null ? "Not Tested" : this.state.CylinderInstall === true ? "Pass" : "Fail"}

                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Adjust Credit" && (
                          <Badge
                            className={`text-xs ${this.state.AdjustCredit === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.AdjustCredit === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.AdjustCredit === null ? "Not Tested" : this.state.AdjustCredit === true ? "Pass" : "Fail"}

                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Child Lock" && (
                          <Badge
                            className={`text-xs ${this.state.ChildLock === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.ChildLock === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.ChildLock === null ? "Not Tested" : this.state.ChildLock === true ? "Pass" : "Fail"}

                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Change Battery" && (
                          <Badge
                            className={`text-xs ${this.state.ChangeBattery === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.ChangeBattery === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.ChangeBattery === null ? "Not Tested" : this.state.ChangeBattery === true ? "Pass" : "Fail"}

                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}

                        {job.name === "Get Cumulative Report" && (
                          <Badge
                            className={`text-xs ${this.state.CumulativeReport === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.CumulativeReport === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.CumulativeReport === null ? "Not Tested" : this.state.CumulativeReport === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Get Hardware Status" && (
                          <Badge
                            className={`text-xs ${this.state.HardwareStatus === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.HardwareStatus === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.HardwareStatus === null ? "Not Tested" : this.state.HardwareStatus === true ? "Pass" : "Fail"}

                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Get Settings" && (
                          <Badge
                            className={`text-xs ${this.state.GetSettings === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.GetSettings === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.GetSettings === null ? "Not Tested" : this.state.GetSettings === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Get Meter Status" && (
                          <Badge
                            className={`text-xs ${this.state.MeterStatus === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.MeterStatus === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.MeterStatus === null ? "Not Tested" : this.state.MeterStatus === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Get Logs" && (
                          <Badge
                            className={`text-xs ${this.state.GetLogs === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.GetLogs === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.GetLogs === null ? "Not Tested" : this.state.GetLogs === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Open Meter Lock" && (
                          <Badge
                            className={`text-xs ${this.state.OpenMeterLock === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.OpenMeterLock === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.OpenMeterLock === null ? "Not Tested" : this.state.OpenMeterLock === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Adjust Data" && (
                          <Badge
                            className={`text-xs ${this.state.AdjustData === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.AdjustData === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.AdjustData === null ? "Not Tested" : this.state.AdjustData === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Deactivate" && (
                          <Badge
                            className={`text-xs ${this.state.Deactivate === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.Deactivate === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.Deactivate === null ? "Not Tested" : this.state.Deactivate === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Reset All" && (
                          <Badge
                            className={`text-xs ${this.state.ResetAll === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.ResetAll === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.ResetAll === null ? "Not Tested" : this.state.ResetAll === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "OTA" && (
                          <Badge
                            className={`text-xs ${this.state.OTA === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.OTA === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.OTA === null ? "Not Tested" : this.state.OTA === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                        {job.name === "Cooking Sessions" && (
                          <Badge
                            className={`text-xs ${this.state.cookingSessionsShow === null ? "bg-blue-500 hover:bg-blue-600 text-white" : this.state.cookingSessionsShow === true
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                          >
                            {this.state.cookingSessionsShow === null ? "Not Tested" : this.state.cookingSessionsShow === true ? "Pass" : "Fail"}
                            {/* {job.status.toUpperCase()} */}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                  </ScrollArea>

                  <Separator className="my-4 flex-shrink-0" />

                  <Button
                    onClick={this.handleExecuteClick}
                    disabled={this.state.selectedJobs.length === 0}
                    className="w-full flex items-center space-x-2 flex-shrink-0"
                    style={{ backgroundColor: '#1a73e8', color: '#ffffff' }}

                  >
                    <Play className="h-4 w-4" />
                    <span>
                      Execute Selected Jobs ({this.state.selectedJobs.length})
                    </span>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Meter Logs */}
            <div className="lg:col-span-2 flex flex-col min-h-0">
              <div className="grid grid-rows-2 gap-4 h-full">
                {this.props.meterLogFiles.map((logFile) => (
                  <Card key={logFile.id} style={{ width: "90%", maxWidth: "900px" }} className="flex-1 flex flex-col min-h-0">
                    <CardHeader className="flex-shrink-0 pb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center space-x-2">
                            <span>{logFile.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {logFile.type.toUpperCase()}
                            </Badge>
                          </CardTitle>
                          <CardDescription>
                            Last updated:
                            {logFile?.id === "esp32-logs" ? this.state.EspTimestamp : this.state.STMTimestamp}
                            {/* {logFile.lastUpdated} */}
                          </CardDescription>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => this.handleRefreshLogs(logFile.id)}
                            disabled={this.state.loadingLogs === logFile.id}
                            className="flex items-center space-x-1"
                          >
                            <RefreshCw className={`h-3 w-3 ${this.state.loadingLogs === logFile.id ? 'animate-spin' : ''}`} />
                            <span className="text-xs">Refresh</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => this.handleDownloadLog(logFile)}
                            disabled={this.state.loadingLogs === logFile.id}
                            className="flex items-center space-x-1"
                          >
                            <Download className="h-3 w-3" />
                            <span className="text-xs">Download</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => this.handleViewLog(logFile.id)}
                            disabled={this.state.loadingLogs === logFile.id}
                            className="flex items-center space-x-1"
                          >
                            <Eye className="h-3 w-3" />
                            <span className="text-xs">View</span>
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col min-h-0 pt-0">
                      <ScrollArea className="flex-1 min-h-0">
                        <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                          {logFile?.id === "esp32-logs" ? this.stripAnsi(this.state.EspResponse) : this.stripAnsi(this.state.STMResponse)}
                        </pre>
                        <div className="space-y-2 pr-2">
                          {/* {this.state.loadingLogs === logFile.id ? (
                            <div className="text-center text-muted-foreground py-8">
                              <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin" />
                              <p>Loading logs...</p>
                            </div>
                          ) : logFile.logs.length === 0 ? (
                            <div className="text-center text-muted-foreground py-8">
                              <Monitor className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p>No logs available</p>
                              <p className="text-sm">Execute jobs to generate logs</p>
                            </div>
                          ) : (
                             logFile.logs.map((log) => (
                              <div
                                key={log.id}
                                className="flex items-start space-x-3 p-3 rounded-lg border bg-card"
                              >
                                {this.getLogIcon(log.level)}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <Badge variant={this.getLogBadgeVariant(log.level)} className="text-xs">
                                      {log.level.toUpperCase()}
                                    </Badge>
                                    <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                      <Clock className="h-3 w-3" />
                                      <span>{log.timestamp}</span>
                                    </div>
                                  </div>
                                  <p className="text-sm">{log.message}</p>
                                </div>
                              </div>
                            ))
                          )} */}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

  }
}
export default JobsPage;
